import React, { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';

import OnFileDownloadIcon from '../../../icons/OnFileDownloadIcon';
import OnFileUploadIcon from '../../../icons/OnFileUploadIcon';
import {
    clearCourseAuditData,
    clearSelfEmployedCourseModules,
    loadCourseAuditData,
    searchSelfEmployedCourseModules,
    updateSelfEmployedCourseModules
} from '../../../store/courseService';
import {
    loadContractDetailsForContractIds,
    loadSelfEmployedManagementCourseModuleStatuses,
    loadServiceDetailsByContract
} from '../../../store/directusService';
import { compareUnorderedArrays } from '../../../utils/arrayUtils';
import { countDays, reverseFormatDate, todayDate } from '../../../utils/dateFunctions';
import { getNameFromId } from '../../../utils/directusFunctions';
import { downloadCsv } from '../../../utils/downloadCSV';
import { FIFTY_TABLE_ROWS_PER_PAGE } from '../../../utils/formValidation/commonConstants';
import { NO_UPDATE_ERROR } from '../../../utils/formValidation/commonErrorConstants';
import {
    CONTRACT_DETAILS_ERROR,
    SELF_EMPLOYED_COURSE_ERROR,
    SELF_EMPLOYED_MANAGEMENT_COURSE_MODULE_STATUS_ERROR,
    SERVICES_ERROR
} from '../../../utils/formValidation/loadingErrorMessageConstants';
import { trimEntries } from '../../../utils/objectUtils';
import { stableSort } from '../../../utils/sortFunctions';
import { FINANCE, hasRole, MANAGER, QUALITY, SUPERUSER } from '../../../utils/userRoles';
import AuditSelfEmployedCoachModal from '../../auditing/auditSelfEmployedCoach/AuditSelfEmployedCoachModal';
import Button from '../../formElements/Button';
import MultiSelect from '../../formElements/MultiSelect';
import SingleSelect from '../../formElements/SingleSelect';
import Switch from '../../formElements/Switch';
import ResultsTable from '../../table/ResultsTable';
import LoadingSpinner from '../../ui/LoadingSpinner';

import SelfEmployedExtractionModal from './SelfEmployedExtractionModal';
import SelfEmployedRow from './SelfEmployedRow';
import { validateFilters } from './validateSearch';

import app from '../../../app.module.css';
import form from '../../../commonStyles/formStyles.module.css';
import local from './selfEmployed.module.css';

const headCells = [
    { id: 'courseInstanceCode', numeric: false, label: 'ID' },
    { id: 'moduleName', numeric: false, label: 'Module Name' },
    { id: 'moduleDate', numeric: false, label: 'Module Date' },
    { id: 'moduleTime', numeric: false, label: 'Module Time' },
    { id: 'trainerName', numeric: false, label: 'Trainer Name' },
    { id: 'approvalStatus', numeric: false, label: 'Approval Status' }
];

const initialRowMetaData = {
    order: 'desc',
    orderBy: 'moduleDate',
    page: 0,
    rowsPerPage: FIFTY_TABLE_ROWS_PER_PAGE
};

const csvHeaderFields =
    'Services,Trainer,Course Name,Self Employed Course Module Instance Id,Module Name,Module Date/Time,Current Payment Status,Current Payment Status Date,Price';

const acceptedRoles = [FINANCE, MANAGER, QUALITY, SUPERUSER];
const csvRoles = [FINANCE, QUALITY, SUPERUSER];

const SelfEmployed = () => {
    // HOOKS
    const dispatch = useDispatch();

    // LOCAL STATE
    const [rows, setRows] = useState([]);
    const [updatedRows, setUpdatedRows] = useState([]);
    const [rowMetaData, setRowMetaData] = useState(initialRowMetaData);
    const [contractIds, setContractIds] = useState([]);
    const [serviceIds, setServiceIds] = useState([]);
    const [approvalStatusId, setApprovalStatusId] = useState('');
    const [serviceDetailsForSelectedContracts, setServiceDetailsForSelectedContracts] = useState(
        []
    );
    const [errors, setErrors] = useState({});
    const [filters, setFilters] = useState({});
    const [isDisabled, setIsDisabled] = useState(false);
    const [isCSVDisabled, setIsCSVDisabled] = useState(false);
    const [loadingMessage, setLoadingMessage] = useState('');
    const [auditDisplayFields, setAuditDisplayFields] = useState(false);
    const [showAuditModal, setShowAuditModal] = useState(false);
    const [auditLabel, setAuditLabel] = useState('');
    const [showExtractionModal, setShowExtractionModal] = useState(false);

    // STORE STATE
    const loggedInUser = useSelector((state) => state.entities.userService.loggedInUser);
    const roles = loggedInUser.roles;
    const contractDetails = useSelector((state) => state.entities.directusService.contractDetails);
    const selfEmployedManagementCourseModuleStatuses = useSelector(
        (state) => state.entities.directusService.selfEmployedManagementCourseModuleStatuses
    );
    const serviceDetails = useSelector((state) => state.entities.directusService.serviceDetails);
    const selfEmployedCourseModules = useSelector(
        (state) => state.entities.courseService.selfEmployedCourseModules
    );
    const selfEmployedCourseModulesMetaData = useSelector(
        (state) => state.entities.courseService.selfEmployedCourseModulesMetaData
    );
    const loadingCourseModules = useSelector(
        (state) => state.entities.courseService.loadingSelfEmployedCourseModules
    );
    const users = useSelector((state) => state.entities.userService.usersByLoggedInUserServiceIds);
    const successMessage = useSelector((state) => state.entities.formsState.successMessage);

    // USEEFFECTS
    useEffect(() => {
        !selfEmployedManagementCourseModuleStatuses?.length &&
            dispatch(loadSelfEmployedManagementCourseModuleStatuses());

        return () => cleanUp();
    }, []);

    useEffect(() => {
        setIsDisabled(!hasRole(acceptedRoles, roles));
        setIsCSVDisabled(!hasRole(csvRoles, roles));
    }, [roles]);

    useEffect(() => {
        if (!loggedInUser.contractIds?.length) return;

        dispatch(loadContractDetailsForContractIds(loggedInUser.contractIds));
        dispatch(loadServiceDetailsByContract(loggedInUser.contractIds));
    }, [loggedInUser.contractIds]);

    useEffect(() => {
        if (
            Object.keys(filters).length &&
            selfEmployedCourseModules.length > 0 &&
            !loadingCourseModules &&
            'number' in selfEmployedCourseModulesMetaData &&
            !selfEmployedCourseModulesMetaData.last
        )
            dispatch(
                searchSelfEmployedCourseModules(
                    filters,
                    selfEmployedCourseModulesMetaData?.number + 1
                )
            );
        else if (selfEmployedCourseModulesMetaData?.last && selfEmployedCourseModules.length > 0) {
            const configuredRows = selfEmployedCourseModules.map((el) => {
                const user = users.find((entry) => entry.id === el.trainerId);
                const trainerName = !user ? '' : `${user.firstName} ${user.lastName}`;
                const serviceNames = el.serviceIds
                    .map((entry) => getNameFromId(serviceDetails, entry))
                    .join(' | ');
                return {
                    ...el,
                    serviceNames,
                    trainerName
                };
            });
            setRows(configuredRows);
        } else {
            rows.length && setRows([]);
            if (Object.keys(filters).length) setLoadingMessage(SELF_EMPLOYED_COURSE_ERROR);
            else setLoadingMessage('Select filters to search...');
        }
    }, [selfEmployedCourseModules, selfEmployedCourseModulesMetaData, loadingCourseModules]);

    useEffect(() => {
        if (successMessage === 'Self employed course modules have been updated') setUpdatedRows([]);
    }, [successMessage]);

    // HELPER FNS
    const createRows = () =>
        stableSort(rows, rowMetaData.orderBy, rowMetaData.order).map((el) => (
            <SelfEmployedRow
                key={el.id}
                row={el}
                isDisabled={isDisabled}
                isCSVDisabled={isCSVDisabled}
                onUpdateApprovalStatus={onUpdateApprovalStatus}
                isAuditable={auditDisplayFields}
                onSearchAuditItems={onSearchAuditItems}
            />
        ));

    const clearError = (key) => {
        if (key in errors && errors[key].error)
            setErrors((prev) => ({ ...prev, [key]: { error: false } }));
    };

    const resetData = () => {
        rows.length && setRows([]);
        selfEmployedCourseModules?.length && dispatch(clearSelfEmployedCourseModules());
    };

    const loadingError = () => {
        if (!contractDetails?.length) return CONTRACT_DETAILS_ERROR;
        if (!serviceDetails?.length) return SERVICES_ERROR;
        if (!selfEmployedManagementCourseModuleStatuses?.length)
            return SELF_EMPLOYED_MANAGEMENT_COURSE_MODULE_STATUS_ERROR;
    };

    const cleanUp = () => {
        dispatch(clearSelfEmployedCourseModules());
    };

    // EVENT HANDLERS
    const onUpdateFilters = (key, value) => {
        if (value === filters[key]) return;
        resetData();

        if (!value) setFilters(trimEntries([key], filters));
        else setFilters((prev) => ({ ...prev, [key]: value }));
    };

    const onDownloadCSV = () => {
        const name = `Self_Employed_Coach_Report_${format(new Date(), `dd_MMM_yyyy_HH_mm`)}`;

        const rowsWithin30Days = rows
            .filter(
                (el) =>
                    el.approvalStatus === 'Approved' ||
                    el.approvalStatus === 'Logged' ||
                    (el.approvalStatus === 'Paid' && countDays(el.moduleDate, todayDate()) < 31) ||
                    (el.approvalStatus === 'Rejected' && countDays(el.moduleDate, todayDate()) < 31)
            )
            .map((el) => [
                el.serviceNames,
                el.trainerName,
                el.courseName,
                el.id,
                el.moduleName,
                `${reverseFormatDate(el.moduleDate)} ${el.moduleTime}`,
                el.approvalStatus,
                reverseFormatDate(el.approvalStatusDate),
                el.payRates
            ]);
        downloadCsv(csvHeaderFields, rowsWithin30Days, name);
    };

    const onContractsChange = (ids) => {
        if (!ids.length || compareUnorderedArrays(ids, contractIds)) {
            setContractIds([]);
            onUpdateFilters('contractIds');
            setServiceIds([]);
            setServiceDetailsForSelectedContracts([]);
            onUpdateFilters('serviceIds');
            return;
        }

        setContractIds(ids);
        setServiceIds([]);
        onUpdateFilters('contractIds', ids);
        onUpdateFilters('serviceIds');
        setServiceDetailsForSelectedContracts(
            serviceDetails.filter((el) => ids.find((entry) => entry === el.contract.id))
        );
    };

    const onServicesChange = (ids) => {
        if (!ids.length || compareUnorderedArrays(ids, serviceIds)) {
            setServiceIds([]);
            onUpdateFilters('serviceIds');
            return;
        }

        setServiceIds(ids);
        onUpdateFilters('serviceIds', ids);
    };

    const onApprovalStatusChange = (id) => {
        if (!id.length || id === approvalStatusId) {
            setApprovalStatusId('');
            onUpdateFilters('approvalStatusId');
            return;
        }

        setApprovalStatusId(id);
        onUpdateFilters('approvalStatusId', id);
    };

    // EVENT HANDLER CALLBACKS
    const onUpdateApprovalStatus = (id, statusId) => {
        const modifiedRow = { id, approvalStatusId: statusId };
        const rowMultipleChange = updatedRows?.find((el) => el.id === id);

        if (rowMultipleChange)
            setUpdatedRows((updatedRows) =>
                updatedRows.map((el) => (el.id !== id ? el : modifiedRow))
            );
        else setUpdatedRows((updatedRows) => [...updatedRows, modifiedRow]);
    };

    const onSearchAuditItems = (id, label) => {
        setShowAuditModal(true);
        dispatch(loadCourseAuditData('self-employed-course-module', id, label));
        setAuditLabel(label);
    };

    const onAuditModalClose = (e, reason) => {
        if (reason === 'backdropClick') return;
        setShowAuditModal(false);
        setAuditLabel('');
        dispatch(clearCourseAuditData());
    };

    const onSwitchAuditDisplayFields = (e) => {
        setAuditDisplayFields(e.target.checked);
    };

    const onUploadModalClose = (e, reason) => {
        if (reason && reason === 'backdropClick') return;
        setShowExtractionModal(false);
    };

    const onSearchSelfEmployed = (e) => {
        e.preventDefault();
        setUpdatedRows([]);
        const newErrors = validateFilters(filters);
        if (Object.values(newErrors).some((el) => el.error)) {
            setErrors(newErrors);
            return;
        }
        dispatch(searchSelfEmployedCourseModules(filters, 0));
    };

    const onUpdateSelfEmployedStatus = () => {
        const modifiedRows = updatedRows.filter((el) =>
            rows.find(
                (entry) =>
                    entry.id === el.id &&
                    entry.approvalStatusId !== el.approvalStatusId &&
                    el.approvalStatusId
            )
        );

        if (modifiedRows.length) {
            dispatch(updateSelfEmployedCourseModules({ updates: modifiedRows }));
        } else setErrors({ updateButton: { error: true, message: NO_UPDATE_ERROR } });
    };

    // AWAITING CONTENT
    const errorMsg = loadingError();
    if (errorMsg) return <LoadingSpinner content={errorMsg} />;

    // RENDER
    return (
        <div className={app.pageContainer}>
            <div className={local.headerBar}>
                <div className={app.sectionHeading}>Self Employed Management Queue</div>

                <div className={local.headerContentRight} data-testid="form_start">
                    <div>
                        <label className={local.label}>Export to CSV</label>

                        <div className={local.downloadIconWrapper}>
                            <OnFileDownloadIcon
                                active={
                                    !isCSVDisabled &&
                                    selfEmployedCourseModules?.length > 0 &&
                                    selfEmployedCourseModulesMetaData?.last &&
                                    ('contractIds' in filters ||
                                        'serviceIds' in filters ||
                                        'approvalStatusId' in filters)
                                }
                                onDownload={onDownloadCSV}
                            />
                        </div>
                    </div>
                    <div>
                        <label className={local.label}>All to Paid Finance Upload</label>

                        <div className={local.downloadIconWrapper}>
                            <OnFileUploadIcon
                                active={
                                    !isCSVDisabled &&
                                    !!selfEmployedManagementCourseModuleStatuses?.length
                                }
                                onUpload={() => setShowExtractionModal(true)}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <form onSubmit={onSearchSelfEmployed} data-testid="form_start_self_employed_management">
                <div className={form.formSection}>
                    <div className={form.formColumn}>
                        <MultiSelect
                            id="contracts"
                            label="Contracts"
                            placeholder="Select contracts..."
                            disabled={isDisabled}
                            menuItems={contractDetails || []}
                            preSelectedIds={contractIds || []}
                            preSelects={
                                contractDetails.filter((el) => contractIds.includes(el.id)) || []
                            }
                            onChange={(chosenIds) => onContractsChange(chosenIds)}
                        />
                        <MultiSelect
                            id="services"
                            label="Services"
                            placeholder="Select services..."
                            disabled={isDisabled || !contractIds.length}
                            menuItems={serviceDetailsForSelectedContracts || []}
                            preSelectedIds={serviceIds || []}
                            preSelects={
                                serviceDetailsForSelectedContracts.filter((el) =>
                                    serviceIds.includes(el.id)
                                ) || []
                            }
                            onChange={(chosenIds) => onServicesChange(chosenIds)}
                        />
                    </div>
                    <div className={form.formColumn}>
                        <SingleSelect
                            id={'approvalStatusId'}
                            label="Approval Status"
                            placeholder="Select status..."
                            disabled={isDisabled}
                            menuItems={selfEmployedManagementCourseModuleStatuses}
                            selectedId={approvalStatusId || ''}
                            selected={selfEmployedManagementCourseModuleStatuses.find(
                                (el) => el.id === approvalStatusId
                            )}
                            onChange={(chosenId) => onApprovalStatusChange(chosenId)}
                        />
                    </div>
                </div>
                <div className={local.searchToolbar}>
                    <div className={local.customButtonMargin}>
                        <Button
                            id="selfEmployedSearch"
                            content="Search"
                            disabled={isDisabled}
                            error={errors.searchButton}
                            clearError={() => clearError('searchButton')}
                            onClick={onSearchSelfEmployed}
                        />
                    </div>

                    <div hidden={!rows.length}>
                        <Switch
                            id={'auditDisplayFields'}
                            label="Show Audit Fields"
                            onOff={true}
                            customClass="auditSwitch"
                            checked={auditDisplayFields}
                            onChange={onSwitchAuditDisplayFields}
                        />
                    </div>
                </div>
            </form>
            {rows.length < 1 ? (
                <LoadingSpinner content={loadingMessage} />
            ) : (
                <ResultsTable
                    defaultOrderBy={rowMetaData.orderBy}
                    headCells={headCells}
                    passRowMetaDataUp={setRowMetaData}
                    tableRows={createRows()}
                    totalResults={rows.length}
                />
            )}
            <div hidden={!rows.length}>
                <Button
                    id="selfEmployedUpdate"
                    type="button"
                    content="Update"
                    disabled={isDisabled}
                    error={errors.updateButton}
                    clearError={() => clearError('updateButton')}
                    onClick={onUpdateSelfEmployedStatus}
                />
            </div>

            {showAuditModal && (
                <AuditSelfEmployedCoachModal
                    isOpen={showAuditModal}
                    label={auditLabel}
                    onClose={onAuditModalClose}
                />
            )}
            {showExtractionModal && (
                <SelfEmployedExtractionModal
                    isOpen={showExtractionModal}
                    onClose={onUploadModalClose}
                />
            )}
        </div>
    );
};

export default SelfEmployed;
